import type { MenuItems } from "@/modules/menu";

import { Skeleton, Typography } from "antd";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { useGetCurrentUser } from "@/entities/current-user";
import { CurrentUserAvatar } from "@/modules/current-user-avatar";
import {
  useMenuItemAboutAction,
  useMenuItemLink,
  useMenuItemLogoutAction,
  useMenuItemModelLink,
  useMenuSelectedKeys,
} from "@/modules/menu";
import { useThemeBreakpoints } from "@/services/theme";

import { SIDEBAR_WIDTH } from "./app-layout-sidebar.constant";
import { appLayoutSidebarCollapsedAtom } from "./app-layout-sidebar.state";

const { Text } = Typography;

export const useAppLayoutSidebarCollapsed = () => {
  const [collapsed, setCollapsed] = useAtom(appLayoutSidebarCollapsedAtom);

  return {
    collapsed,
    setCollapsed,
  };
};

export const useAppLayoutSidebar = () => {
  const { t } = useTranslation();

  const { isXs } = useThemeBreakpoints();
  const { collapsed, setCollapsed } = useAppLayoutSidebarCollapsed();
  const collapsedWidth = isXs ? 0 : SIDEBAR_WIDTH.COLLAPSED;

  const { currentUser, isLoading } = useGetCurrentUser();
  const userNameAndEmail = !isLoading
    ? `${currentUser?.name} (${currentUser?.email})`
    : "";

  const userLabel = collapsed ? (
    <Text className="separator">
      <CurrentUserAvatar
        type="firstLetter"
        style={{ backgroundColor: "#fde3cf", borderColor: "transparent" }}
        withTooltip
      />
    </Text>
  ) : (
    <Text className="separator" title={userNameAndEmail}>
      {isLoading ? (
        <Skeleton paragraph={false} title={{ width: "120px" }} active />
      ) : (
        currentUser?.name
      )}
    </Text>
  );

  const getSeparatorItem = (text: string) => (
    <Text className="separator">{!collapsed && text}</Text>
  );

  const menuItems: MenuItems = [
    // top-most section
    {
      type: "group",
      children: [useMenuItemLink("dashboard"), useMenuItemLink("mySpace")],
    },

    // browse by
    {
      type: "group",
      label: getSeparatorItem(t("navigation:browse")),
      children: [
        useMenuItemModelLink("organizations"),
        useMenuItemModelLink("locations"),
        useMenuItemModelLink("assets"),
        useMenuItemModelLink("interventions"),
        useMenuItemModelLink("observations"),
        useMenuItemModelLink("projects"),
      ],
    },

    // settings
    {
      type: "group",
      label: getSeparatorItem(t("navigation:administration")),
      children: [
        useMenuItemLink("import"),
        useMenuItemModelLink("users"),
        useMenuItemModelLink("assetTypes"),
        useMenuItemModelLink("technicalForms"),
        useMenuItemModelLink("interventionTemplates"),
        useMenuItemModelLink("activities"),
      ],
    },

    // preferences
    {
      type: "group",
      label: userLabel,
      children: [
        useMenuItemLink("support"),
        useMenuItemLink("preferences"),
        useMenuItemAboutAction(),
        useMenuItemLogoutAction(),
      ],
    },
  ];

  const { selectedKeys } = useMenuSelectedKeys(menuItems);

  return {
    menuItems,
    selectedKeys,
    collapsed,
    collapsedWidth,
    setCollapsed,
    isXs,
  };
};
